import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService, InvitationService } from '@df/api';
import { SIGNUP_MODE } from '../../config/featureFlags';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';
import {
  getScopedInvitationToken,
  hasScopedInvitationContinuation,
  setScopedInvitationContinuation,
} from '../../utils/scopedInvitationSession';

const isDev = process.env.NODE_ENV === 'development';

function logDevError(context, error) {
  if (!isDev) return;
  console.error(context, {
    message: error?.message || 'Unknown error',
    status: error?.status || null,
  });
}

const INVITE_INVALID_MESSAGE = 'Invalid invitation code.';
const INVITE_UNREACHABLE_MESSAGE = 'Server unreachable. Is the backend running?';
const INVITE_UNEXPECTED_MESSAGE = 'Unexpected server response.';

// Self-service signup (OPEN mode with the server switch on). Copy is local: backend exception
// text is never shown.
const DEFAULT_CODE_TTL_SECONDS = 900;
const DEFAULT_RESEND_AFTER_SECONDS = 60;
const SIGNUP_CODE_PATTERN = /^[A-Z0-9]{8}$/;
const SELF_SERVICE_INVALID_EMAIL = 'Enter a valid email address.';
const SELF_SERVICE_CODE_ERROR = 'That code is invalid or has expired. Check the code, or request a new one.';
const SELF_SERVICE_ACCOUNT_ERROR =
  "We couldn't create an account with these details. If you already have an account, sign in or reset your password.";
const SELF_SERVICE_RATE_LIMITED = 'Too many requests. Please wait a few minutes and try again.';
const SELF_SERVICE_TEMPORARILY_UNAVAILABLE = 'Sign-up is temporarily unavailable. Please try again later.';
const SELF_SERVICE_UNREACHABLE = "We couldn't reach DataFre8. Check your connection and try again.";
const SELF_SERVICE_CODE_REQUEST_FAILED = "We couldn't send a code. Please try again.";
const SELF_SERVICE_ACCOUNT_FAILED = "We couldn't create your account. Please try again.";
const SELF_SERVICE_DISABLED_NOTICE =
  "Self-service sign-up isn't available right now. If you have an invitation code, you can use it below.";

const normalizeSignupEmail = (value) => (value || '').trim().toLowerCase();
const normalizeSignupCode = (value) => (value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8);

const firstMessage = (value) => {
  if (Array.isArray(value)) return value.length ? String(value[0]) : '';
  return value ? String(value) : '';
};

const describeCodeRequestError = (error) => {
  const status = Number(error?.status);
  if (isNetworkError(error)) return { message: SELF_SERVICE_UNREACHABLE };
  if (status === 403 && error?.data?.code === 'PUBLIC_SIGNUP_DISABLED') return { disabled: true };
  if (status === 400) return { message: SELF_SERVICE_INVALID_EMAIL };
  if (status === 429) return { message: SELF_SERVICE_RATE_LIMITED };
  // @df/api turns every 5xx into a status-less "temporary server error".
  if (!status) return { message: SELF_SERVICE_TEMPORARILY_UNAVAILABLE };
  return { message: SELF_SERVICE_CODE_REQUEST_FAILED };
};

const describeRegistrationError = (error) => {
  const status = Number(error?.status);
  if (isNetworkError(error)) return SELF_SERVICE_UNREACHABLE;
  if (status === 429) return SELF_SERVICE_RATE_LIMITED;
  const data = error?.data;
  if (status === 400 && data && typeof data === 'object') {
    const codeMessage = firstMessage(data.invitation_code);
    if (codeMessage) {
      // The attempt lockout message is ours and safe to show; every other code error is generic.
      return codeMessage.startsWith('Too many') ? codeMessage : SELF_SERVICE_CODE_ERROR;
    }
    if (data.email || data.username) return SELF_SERVICE_ACCOUNT_ERROR;
    if (data.password) {
      return Array.isArray(data.password) ? data.password.map(String).join(' ') : String(data.password);
    }
  }
  if (!status) return SELF_SERVICE_TEMPORARILY_UNAVAILABLE;
  return SELF_SERVICE_ACCOUNT_FAILED;
};

const SelfServiceSignup = ({ codeTtlSeconds, onUseInvitationCode, onUnavailable }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [requestedEmail, setRequestedEmail] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const requestInFlight = useRef(false);
  const submitInFlight = useRef(false);

  const resendSecondsLeft = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
  const resendPending = resendSecondsLeft > 0;
  const ttlMinutes = Math.round((codeTtlSeconds || DEFAULT_CODE_TTL_SECONDS) / 60);

  useEffect(() => {
    if (!resendPending) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [resendPending]);

  const requestCode = async (targetEmail) => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    setRequestLoading(true);
    setRequestError('');
    try {
      const response = await InvitationService.requestSignupCode(targetEmail);
      const resendAfter = Number(response?.resendAfterSeconds) > 0
        ? Number(response.resendAfterSeconds)
        : DEFAULT_RESEND_AFTER_SECONDS;
      const requestedAt = Date.now();
      setRequestedEmail(targetEmail);
      setNow(requestedAt);
      setResendAvailableAt(requestedAt + resendAfter * 1000);
      setStep('verify');
    } catch (error) {
      logDevError('[Signup] Sign-up code request failed', error);
      const outcome = describeCodeRequestError(error);
      if (outcome.disabled) {
        onUnavailable();
        return;
      }
      setRequestError(outcome.message);
    } finally {
      requestInFlight.current = false;
      setRequestLoading(false);
    }
  };

  const handleRequestCode = (evt) => {
    evt.preventDefault();
    const normalizedEmail = normalizeSignupEmail(email);
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setRequestError(SELF_SERVICE_INVALID_EMAIL);
      return;
    }
    requestCode(normalizedEmail);
  };

  const handleResend = () => {
    if (resendPending || requestLoading) return;
    setCode('');
    setSubmitError('');
    requestCode(requestedEmail);
  };

  const handleChangeEmail = () => {
    setStep('email');
    setRequestError('');
    setSubmitError('');
    setCode('');
  };

  const handleCreateAccount = async (evt) => {
    evt.preventDefault();
    if (submitInFlight.current) return;
    setSubmitError('');

    const normalizedCode = normalizeSignupCode(code);
    if (!SIGNUP_CODE_PATTERN.test(normalizedCode)) {
      setSubmitError('Enter the full 8-character sign-up code.');
      return;
    }
    if (!password || !passwordConfirm) {
      setSubmitError('Enter and confirm a password.');
      return;
    }
    if (password !== passwordConfirm) {
      setSubmitError('Passwords do not match.');
      return;
    }

    submitInFlight.current = true;
    setSubmitLoading(true);
    try {
      await AuthService.registerUser({
        username: requestedEmail,
        email: requestedEmail,
        password,
        re_password: passwordConfirm,
        invitation_code: normalizedCode,
      });
      navigate('/login', { state: { signupComplete: true, email: requestedEmail } });
    } catch (error) {
      logDevError('[Signup] Self-service registration failed', error);
      setSubmitError(describeRegistrationError(error));
    } finally {
      submitInFlight.current = false;
      setSubmitLoading(false);
    }
  };

  return (
    <Box>
      {step === 'email' ? (
        <Box component="form" onSubmit={handleRequestCode} noValidate>
          <TextField
            fullWidth
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(evt) => setEmail(evt.target.value)}
            disabled={requestLoading}
            sx={{ mb: 2 }}
          />
          {requestError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {requestError}
            </Alert>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={requestLoading}>
            {requestLoading ? 'Sending code…' : 'Send code'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleCreateAccount} noValidate>
          <Alert severity="info" role="status" sx={{ mb: 2 }}>
            If {requestedEmail} can be used to create an account, we have sent a sign-up code to it.
            The code expires in {ttlMinutes} minutes.
          </Alert>
          <TextField
            fullWidth
            label="Sign-up code"
            value={code}
            onChange={(evt) => setCode(normalizeSignupCode(evt.target.value))}
            helperText="Enter the 8-character code from the email."
            inputProps={{ autoComplete: 'one-time-code', autoCapitalize: 'characters', spellCheck: false }}
            disabled={submitLoading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(evt) => setPassword(evt.target.value)}
            disabled={submitLoading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(evt) => setPasswordConfirm(evt.target.value)}
            disabled={submitLoading}
            sx={{ mb: 2 }}
          />
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={submitLoading}>
            {submitLoading ? 'Creating account…' : 'Create Account'}
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mt: 1 }}>
            <Button variant="text" onClick={handleResend} disabled={resendPending || requestLoading || submitLoading}>
              {requestLoading
                ? 'Sending code…'
                : resendPending
                ? `Resend code in ${resendSecondsLeft}s`
                : 'Resend code'}
            </Button>
            <Button variant="text" onClick={handleChangeEmail} disabled={submitLoading || requestLoading}>
              Use a different email
            </Button>
          </Box>
          {requestError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {requestError}
            </Alert>
          )}
        </Box>
      )}
      <Button variant="text" fullWidth sx={{ mt: 2 }} onClick={onUseInvitationCode}>
        Already have an invitation code?
      </Button>
      <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
        Back to Login
      </Button>
    </Box>
  );
};

const Signup = () => {
  const navigate = useNavigate();
  const isInviteOnly = SIGNUP_MODE === 'INVITE_ONLY';
  const scopedInvitationToken = getScopedInvitationToken();
  const isScopedContinuation = Boolean(
    scopedInvitationToken && hasScopedInvitationContinuation(),
  );
  // OPEN mode asks the server whether self-service signup is on; INVITE_ONLY and scoped
  // continuation never offer it.
  const canOfferSelfService = !isInviteOnly && !isScopedContinuation;
  const [selfServiceStatus, setSelfServiceStatus] = useState(
    canOfferSelfService ? 'checking' : 'unavailable',
  );
  const [codeTtlSeconds, setCodeTtlSeconds] = useState(DEFAULT_CODE_TTL_SECONDS);
  const [selfServiceNotice, setSelfServiceNotice] = useState('');
  const [useInvitationCode, setUseInvitationCode] = useState(false);
  const checkingSelfService = canOfferSelfService && selfServiceStatus === 'checking';
  const showSelfService = canOfferSelfService && selfServiceStatus === 'available' && !useInvitationCode;
  // Everything that is not self-service or scoped continuation uses the invitation-code flow,
  // including OPEN mode when the server reports signup off.
  const requiresLegacyInviteCode = !isScopedContinuation && !showSelfService && !checkingSelfService;

  useEffect(() => {
    if (!canOfferSelfService) return undefined;
    let cancelled = false;
    InvitationService.getSignupAvailability()
      .then((response) => {
        if (cancelled) return;
        if (Number(response?.codeTtlSeconds) > 0) setCodeTtlSeconds(Number(response.codeTtlSeconds));
        setSelfServiceStatus(response?.publicSignupEnabled === true ? 'available' : 'unavailable');
      })
      .catch((error) => {
        logDevError('[Signup] Sign-up availability check failed', error);
        if (!cancelled) setSelfServiceStatus('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [canOfferSelfService]);

  const handleSelfServiceUnavailable = () => {
    setSelfServiceStatus('unavailable');
    setSelfServiceNotice(SELF_SERVICE_DISABLED_NOTICE);
  };

  const [inviteCode, setInviteCode] = useState('');
  const [inviteValidated, setInviteValidated] = useState(false);
  const [inviteContext, setInviteContext] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleInviteCodeChange = (value) => {
    setInviteCode(value);
    if (inviteValidated) {
      setInviteValidated(false);
      setInviteContext(null);
    }
    setInviteError('');
  };

  const handleValidateInvite = async (evt) => {
    evt.preventDefault();
    const trimmedCode = inviteCode.trim();
    if (!trimmedCode) {
      setInviteError('Please enter an invitation code.');
      return;
    }

    setInviteLoading(true);
    setInviteError('');
    try {
      const response = await InvitationService.validateInviteCode(trimmedCode);
      if (response && typeof response.valid === 'boolean') {
        if (!response.valid) {
          setInviteError(INVITE_INVALID_MESSAGE);
          setInviteValidated(false);
          setInviteContext(null);
          return;
        }
        setInviteContext(response || null);
        setInviteValidated(true);
        return;
      }
      setInviteError(INVITE_UNEXPECTED_MESSAGE);
      setInviteValidated(false);
      setInviteContext(null);
    } catch (error) {
      logDevError('[Signup] Invite validation failed', error);
      const message = isNetworkError(error)
        ? INVITE_UNREACHABLE_MESSAGE
        : getSafeApiErrorMessage(error, INVITE_UNEXPECTED_MESSAGE);
      setInviteError(message);
      setInviteValidated(false);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRegister = async (evt) => {
    evt.preventDefault();
    setSubmitError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !passwordConfirm) {
      setSubmitError('Email and password are required.');
      return;
    }
    if (password !== passwordConfirm) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (requiresLegacyInviteCode && !inviteValidated) {
      setSubmitError('Please validate your invitation code first.');
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        username: trimmedEmail,
        email: trimmedEmail,
        password,
        re_password: passwordConfirm,
      };
      if (isScopedContinuation) {
        payload.scoped_invitation_token = scopedInvitationToken;
      } else if (requiresLegacyInviteCode) {
        payload.invitation_code = inviteCode.trim();
      }
      await AuthService.registerUser(payload);
      if (isScopedContinuation) {
        setScopedInvitationContinuation('verify');
      }
      navigate('/verify-email-pending', { state: { email: trimmedEmail } });
    } catch (error) {
      logDevError('[Signup] Registration failed', error);
      setSubmitError(getSafeApiErrorMessage(error, 'Request failed. Please try again.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #2D5EFF, #EAF1FF)'
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#2D5EFF' }}>
            Sign up
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {isScopedContinuation
              ? 'Create the invited account linked to your scoped invitation.'
              : showSelfService
              ? "Create your DataFre8 account. We'll email you a sign-up code to confirm your address."
              : requiresLegacyInviteCode
              ? 'Enter your invitation code to continue.'
              : 'Create your account to get started.'}
          </Typography>

          {checkingSelfService && (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking sign-up options…</Typography>
            </Box>
          )}

          {showSelfService && (
            <SelfServiceSignup
              codeTtlSeconds={codeTtlSeconds}
              onUseInvitationCode={() => setUseInvitationCode(true)}
              onUnavailable={handleSelfServiceUnavailable}
            />
          )}

          {selfServiceNotice && requiresLegacyInviteCode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {selfServiceNotice}
            </Alert>
          )}

          {requiresLegacyInviteCode && useInvitationCode && selfServiceStatus === 'available' && (
            <Button variant="text" fullWidth sx={{ mb: 2 }} onClick={() => setUseInvitationCode(false)}>
              Get a sign-up code by email instead
            </Button>
          )}

          {requiresLegacyInviteCode && !inviteValidated && (
            <Box component="form" onSubmit={handleValidateInvite} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Invitation Code"
                value={inviteCode}
                onChange={(evt) => handleInviteCodeChange(evt.target.value)}
                disabled={inviteLoading}
                sx={{ mb: 2 }}
              />
              {inviteError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {inviteError}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={inviteLoading}
                fullWidth
              >
                {inviteLoading ? <CircularProgress size={20} /> : 'Validate Code'}
              </Button>
            </Box>
          )}

          {(isScopedContinuation || (requiresLegacyInviteCode && inviteValidated)) && (
            <Box component="form" onSubmit={handleRegister}>
              {isScopedContinuation && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your account will be created first. You will confirm the invitation after email verification.
                </Alert>
              )}
              {inviteContext && requiresLegacyInviteCode && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Invitation code validated.
                </Alert>
              )}
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(evt) => setEmail(evt.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(evt) => setPassword(evt.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={passwordConfirm}
                onChange={(evt) => setPasswordConfirm(evt.target.value)}
                sx={{ mb: 2 }}
              />
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitLoading}
              >
                {submitLoading ? <CircularProgress size={20} /> : 'Create Account'}
              </Button>
              <Button
                variant="text"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Signup;
